import React, { useState, useEffect, useContext } from 'react';
import ArticleBody from './ArticleBody';
import ArticleSideNavigation, { ContactUrl } from './ArticleSideNavigation';
import { CustomerServiceApiImpl } from './CustomerServiceApiImpl';
import { CustomerServiceContext } from './CustomerServiceContext';
import { IArticleResponse, ICategorizedArticle } from './ICustomerService';
import { ICustomerServiceApi } from './ICustomerServiceApi';
import useBreakpoint from '../utils/useBreakPoint';
import { isContactUrl } from './ArticleSideNavigation';
import ContactForm from './ContactForm';
import ContactInfo from './ContactInfo';
import { Trans, useTranslation } from 'react-i18next';
import { en, fr } from './i18n';
import Loading from './Loading';
import LeftChevron from './LeftChevron';
import { openHelpPanel } from '../utils/openHelpPanel';

export interface CustomerServiceArticleProps {
    categorizedArticles: ICategorizedArticle[];
    api?: ICustomerServiceApi;
}

const CustomerServiceArticle = ({
    categorizedArticles,
    api = new CustomerServiceApiImpl(),
}: CustomerServiceArticleProps) => {
    const { i18n, t } = useTranslation('customer-service');
    i18n.addResourceBundle('en', 'customer-service', en, true, true);
    i18n.addResourceBundle('fr', 'customer-service', fr, true, true);

    const { setView, urlName, contact, setContact } = useContext(CustomerServiceContext);

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<IArticleResponse | null>(null);
    const { isMobile } = useBreakpoint();

    useEffect(() => {
        if (isContactUrl(urlName)) {
            setContact(urlName);
            setIsLoading(false);
            return;
        }

        api.getArticle(urlName)
            .then((value) => {
                setData(value);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                window.location.href = '/pages/customer-service';
                setView('main');
            });
    }, [api, setData, setIsLoading, urlName]);

    if (isLoading) {
        return (
            <div className="customer-service-faq__loading">
                <Loading />
            </div>
        );
    }

    return (
        <>
            <div className="customer-service-faq__article">
                {!isMobile && <ArticleSideNavigation categorizedArticles={categorizedArticles} />}

                {data !== null && contact === null && <ArticleBody article={data} />}

                {contact !== null && (
                    <div className="customer-service-faq__article__body">
                        {isMobile && (
                            <button
                                className="customer-service-faq__article__body__nav"
                                onClick={() => {
                                    setView('main');
                                    setContact(null);
                                    window.history.pushState({}, '', '/pages/customer-service');
                                }}
                            >
                                <LeftChevron />
                                <span className="customer-service-faq__article__body__nav__back">
                                    {t('backToHelpCenter')}
                                </span>
                            </button>
                        )}

                        <div className="customer-service-faq__article__body__separator"></div>

                        {contact === 'email-inquiry' && (
                            <ContactForm
                                title={t('contactFormGeneralInquiriesTitle')}
                                subtitle={t('forAnyGeneralInquiries')}
                                type="general"
                            />
                        )}
                        {contact === 'contact-info' && <ContactInfo />}
                        {contact === 'billing-refund-inquiry' && (
                            <ContactForm
                                title={t('contactFormBillingAndRefundInquiriesTitle')}
                                subtitle={
                                    <div>
                                        <Trans
                                            defaults="{{bilingOrRefundInquiriesDesc1}} <0>{{bilingOrRefundInquiriesDesc1Link}}</0> {{bilingOrRefundInquiriesDesc2}} <1>{{bilingOrRefundInquiriesDesc2Link}}</1> {{bilingOrRefundInquiriesDesc3}}"
                                            values={{
                                                bilingOrRefundInquiriesDesc1: t('bilingOrRefundInquiriesDesc1'),
                                                bilingOrRefundInquiriesDesc1Link: t('bilingOrRefundInquiriesDesc1Link'),
                                                bilingOrRefundInquiriesDesc2: t('bilingOrRefundInquiriesDesc2'),
                                                bilingOrRefundInquiriesDesc2Link: t('bilingOrRefundInquiriesDesc2Link'),
                                                bilingOrRefundInquiriesDesc3: t('bilingOrRefundInquiriesDesc3'),
                                            }}
                                            components={[
                                                <a
                                                    className="tsc-contact-form__text__link"
                                                    key="1"
                                                    href="https://www.tsc.ca/pages/customer-service/refunds?ic=web-to-case"
                                                >
                                                    {t('bilingOrRefundInquiriesDesc1Link')}
                                                </a>,
                                                <button
                                                    className="tsc-contact-form__button__live-chat"
                                                    key="2"
                                                    onClick={openHelpPanel}
                                                >
                                                    {t('bilingOrRefundInquiriesDesc2Link')}
                                                </button>,
                                            ]}
                                        />
                                    </div>
                                }
                                type="billing"
                            />
                        )}
                        {contact === 'send-feedback' && (
                            <ContactForm
                                title={t('contactFormSendFeedbackTitle')}
                                subtitle={
                                    <div>
                                        <Trans
                                            defaults="{{provideFeedbackDesc1}} <0>{{provideFeedbackDesc1Link}}</0> {{provideFeedbackDesc2}}"
                                            values={{
                                                provideFeedbackDesc1: t('provideFeedbackDesc1'),
                                                provideFeedbackDesc1Link: t('provideFeedbackDesc1Link'),
                                                provideFeedbackDesc2: t('provideFeedbackDesc2'),
                                            }}
                                            components={[
                                                <a
                                                    className="tsc-contact-form__text__link"
                                                    key="1"
                                                    href="https://www.tsc.ca/pages/customer-service?ic=web-to-case"
                                                >
                                                    {t('provideFeedbackDesc1')}
                                                </a>,
                                            ]}
                                        />
                                    </div>
                                }
                                type="feedback"
                            />
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default CustomerServiceArticle;
